/**
 * Owner: Masa Bayu
 * File: text-authoring-mapping-registry.ts
 * Date:Time: 2026-09-20 22:02
 * Description: CA-09 Q1 — exhaustive Authoring concept → QXW target registry.
 */
/**
 * Exhaustive Phase 1 authoring concepts (CA-02…08) with explicit QXW targets.
 * Unlisted authoring types → REJECT (fail-closed).
 */
export const QX_AUTHORING_CONCEPT_MAPPINGS = [
    {
        conceptId: 'mark',
        caGate: 'CA-05 / 1A',
        qxwTarget: 'QXTextMark { type } on editable inline.marks[]',
    },
    {
        conceptId: 'paragraph',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextParagraphBlock on QXTextContent.blocks[]',
    },
    {
        conceptId: 'heading',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextHeadingBlock { level, inlines }',
    },
    {
        conceptId: 'quote',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextQuoteBlock',
    },
    {
        conceptId: 'code',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextCodeBlock (type "code")',
    },
    {
        conceptId: 'list',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextListBlock { items }',
    },
    {
        conceptId: 'list-item',
        caGate: 'CA-03 / 1B',
        qxwTarget: 'QXTextListItemBlock under list.items[]',
    },
    {
        conceptId: 'text',
        caGate: 'CA-03 / 1A',
        qxwTarget: 'QXTextTextInline { text, marks }',
    },
    {
        conceptId: 'link',
        caGate: 'CA-05 / 1C',
        qxwTarget: 'QXTextLinkInline { kind, target, title?, text, marks }',
    },
    {
        conceptId: 'figure',
        caGate: 'CA-06 / 1D',
        qxwTarget: 'QXTextFigureBlock { assetRef?, alt?, caption? }',
    },
    {
        conceptId: 'assetRef',
        caGate: 'CA-06 / 1D',
        qxwTarget: 'QXTextAssetRef { id } on figure',
    },
    {
        conceptId: 'caption',
        caGate: 'CA-06 / 1D',
        qxwTarget: 'QXTextCaptionBlock under figure.caption only',
    },
    {
        conceptId: 'alt',
        caGate: 'CA-06 / CA-08 / 1D·1F',
        qxwTarget: 'figure.alt string (accessible name; caption ≠ alt)',
    },
    {
        conceptId: 'language',
        caGate: 'CA-07 / 1E',
        qxwTarget: 'QXTextLanguage { languageCode } on content/block/caption',
    },
    {
        conceptId: 'footnote',
        caGate: 'CA-03 / CA-08 / 1F',
        qxwTarget: 'QXTextFootnoteBlock { id, inlines }',
    },
    {
        conceptId: 'endnote',
        caGate: 'CA-03 / CA-08 / 1F',
        qxwTarget: 'QXTextEndnoteBlock { id, inlines }',
    },
    {
        conceptId: 'note-ref',
        caGate: 'CA-03 / CA-08 / 1F',
        qxwTarget: 'QXTextNoteRefInline { targetRef } → footnote|endnote.id',
    },
    {
        conceptId: 'reading-order',
        caGate: 'CA-08 / 1F',
        qxwTarget: 'Derived from PSA + blocks/inlines/figure→caption order (no a11yOrder SoT)',
        notes: 'Not a stored QXW field',
    },
    {
        conceptId: 'derived-role',
        caGate: 'CA-08 / 1F',
        qxwTarget: 'Derived from semantic type registry (not stored; no aria-* bags)',
        notes: 'Not a stored QXW field',
    },
    {
        conceptId: 'text-content',
        caGate: 'CA-02 / 1*',
        qxwTarget: 'QXTextContent { blocks, language? } on text element.content',
    },
];
const CONCEPT_IDS = new Set(QX_AUTHORING_CONCEPT_MAPPINGS.map((entry) => entry.conceptId));
/** Wire `type` values that map to registered block/inline concepts. */
export const QX_AUTHORING_MAPPED_BLOCK_TYPES = [
    'paragraph',
    'heading',
    'quote',
    'code',
    'list',
    'figure',
    'footnote',
    'endnote',
    'rule',
];
export const QX_AUTHORING_MAPPED_INLINE_TYPES = ['text', 'link', 'note-ref'];
export const QX_AUTHORING_MAPPED_NESTED_TYPES = ['list-item', 'caption'];
export function isQXAuthoringConceptMapped(conceptId) {
    return CONCEPT_IDS.has(conceptId);
}
export function requireQXAuthoringConceptMapped(conceptId) {
    if (!isQXAuthoringConceptMapped(conceptId)) {
        throw new Error(`unmapped authoring concept: "${conceptId}"`);
    }
}
export function getQXAuthoringConceptMapping(conceptId) {
    return (QX_AUTHORING_CONCEPT_MAPPINGS.find((entry) => entry.conceptId === conceptId) ?? null);
}
//# sourceMappingURL=text-authoring-mapping-registry.js.map